import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useEffect, useReducer, useState } from "react";

import ReactBSAlert from "react-bootstrap-sweetalert";
import NotificationAlertOptions from "@/layouts/Alerts";
import {
  getCategories,
  updateCategory,
  updateSubategory,
  deleteCategory,
  deleteSubcategory,
  clearMessage,
} from "@/services/categoryServices/categoryAction";
import { getAllProperties } from "@/services/productServices/productActions";
import {
  getEntityPendingChange,
  clearEntityPendingChange,
} from "@/services/pendingChangesServices/pendingChangesActions";
import {
  findOverlayRow,
  getTaxonomyOverlays,
  type TaxonomyOverlayRow,
} from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";
import ReferrersDialog from "@/components/ReferrersDialog";
import { isDeleteRefusal, refusalMessage } from "@/services/deleteRefusal";
import UpdateCategoriesView from "./UpdateCategoriesView";
import { categoryForm, type CategoryOption, type SubcategoryRecord } from "./categoryForm";
import { propertyRows, type PropertyRecord } from "./propertyRows";

const formActions = categoryForm.actions;

const NO_OVERLAYS: TaxonomyOverlayRow[] = [];


const UpdateCategoriesContainer = () => {
  const dispatch = useAppDispatch();


  const categoriesSlice = useAppSelector((s) => s.categories || {}) as any;
  const productsSlice   = useAppSelector((s) => s.products  || {}) as any;
  const errorsSlice     = useAppSelector((s) => s.errors    || {}) as any;
  const permission      = useCurrentPermission();
  const currentSite     = useAppSelector((s) => s.sites?.site || {});
  const entityPendingChange = useAppSelector((s) => s.pendingChanges?.entityPendingChange || null) as any;

  // SCRUM-2127: the form state is one createSlice (categoryForm.ts), mounted
  // locally; the handlers below only dispatch its named actions.
  const [form, dispatchForm] = useReducer(categoryForm.reducer, categoryForm.getInitialState());
  const { activeEntity, idCategory, category, idSubcategory, subcategory, subProperties, subListing, subBox, labelsub, labelslugsub } = form;

  const [openedCollapses, setOpenedCollapses] = useState<unknown[]>([]);
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);
  // The search box's query, with the store list it was typed against: a fresh
  // list from the store (after a save or delete) shows unfiltered again.
  const [search, setSearch] = useState<{ query: string; against: unknown } | null>(null);

  // SCRUM-1851: the site's TaxonomyOverlay rows (the SCRUM-1715 shape) — the
  // subcategory label fields are seeded from this site's row and from nothing
  // else; a node without a row starts empty.
  const [siteOverlays, setSiteOverlays] = useState(NO_OVERLAYS);

  useEffect(() => {
    const siteId = currentSite?._id;
    if (!siteId) return undefined;
    let cancelled = false;
    getTaxonomyOverlays(siteId)
      .then((rows) => {
        if (!cancelled) setSiteOverlays(rows);
      })
      .catch(() => {
        // No rows reads as "not labelled": the fields start empty.
        if (!cancelled) setSiteOverlays(NO_OVERLAYS);
      });
    return () => {
      cancelled = true;
    };
  }, [currentSite?._id]);

  const handleUnlock = (key: string) => dispatchForm(formActions.fieldUnlocked(key));


  // Load categories & properties
  useEffect(() => {
    dispatch(getCategories());
    dispatch(getAllProperties());
    return () => {
      dispatch(clearEntityPendingChange());
    };
  }, [dispatch]);

  // Categories from the store, derived during render.
  const categoriesConst: CategoryOption[] = (categoriesSlice.categories || []).map((c) => ({
    value: { _id: c._id, name: c.name, slug: c.slug, subcategories: c.subcategories },
    label: c.name,
  }));
  let categories = categoriesConst;
  if (search && search.against === categoriesSlice.categories) {
    const q = search.query;
    const base = [...categoriesConst].reverse();
    categories = q.length > 0 ? base.filter((c) => c.label.toLowerCase().includes(q)) : base;
  }

  // Success messages
  useEffect(() => {
    const msg = categoriesSlice?.message;
    if (msg) {
      NotificationAlertOptions("success", msg);
      dispatch(clearMessage());
      dispatch(getCategories());
      if (activeEntity.type && activeEntity.id) {
        dispatch(getEntityPendingChange(activeEntity.type, activeEntity.id));
      }
    }
  }, [categoriesSlice?.message, dispatch, activeEntity]);

  // Error handling
  useEffect(() => {
    if (errorsSlice?.errors?.length) {
      const errs = errorsSlice.errors;
      dispatchForm(formActions.serverErrorsReceived(errs));
    }
  }, [errorsSlice, dispatch]);

  // Properties from the store; the `added` flag is derived from subProperties
  // during render, so no effect has to keep it in sync (shared with Add, SCRUM-2217).
  const allProperties = propertyRows(productsSlice.properties as PropertyRecord[] | undefined, subProperties);


  const collapsesToggle = (collapse: unknown) => {
    setOpenedCollapses((prev) =>
      prev.includes(collapse) ? [] : [collapse]
    );
  };

  const showUpdatingCategory = (e: React.MouseEvent, ch: CategoryOption) => {
    e.preventDefault();
    dispatchForm(formActions.categoryOpened(ch.value));
    dispatch(getEntityPendingChange("category", ch.value._id))
      .finally(() => dispatchForm(formActions.pendingChangeLoaded()));
  };

  const showUpdatingSubCategory = (e: React.MouseEvent, ch: SubcategoryRecord) => {
    e.preventDefault();

    // SCRUM-1412: the payload sends the label states, not the inputs, so they
    // must be seeded for THIS site on every open — and reset to "" when there
    // is nothing, or the previous subcategory's typed label would be written
    // onto this one. SCRUM-1851: the seed is the site's overlay row, never the
    // node's stored `label[]` (gone from the schema with SCRUM-1716).
    const currentSiteLabel = findOverlayRow(siteOverlays, "subcategory", ch._id);

    dispatchForm(
      formActions.subcategoryOpened({
        subcategory: ch,
        countryCode: currentSite?.country_code,
        label: currentSiteLabel?.label || "",
        labelSlug: currentSiteLabel?.slug || "",
      })
    );

    dispatch(getEntityPendingChange("subcategory", ch._id))
      .finally(() => dispatchForm(formActions.pendingChangeLoaded()));
  };

  const onChangeCategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatchForm(formActions.categoryFieldChanged({ name, value }));
  };

  const onChangeSubcategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    dispatchForm(formActions.subcategoryFieldChanged({ name, value, checked: Boolean(checked) }));
  };

  const onClickUpdateCategory = () => {
    dispatch(updateCategory({ _id: idCategory, name: category.name, slug: category.slug }));
  };

  const onClickUpdateSubcategory = () => {
    const filteredProps = (subcategory.properties || []).filter(
      (p) => p.key !== currentSite?.country_code
    );
    const storedEntry = (subcategory.properties || []).find(
      (p) => p.key === currentSite?.country_code
    );
    // Spread the stored entry so keys this screen does not edit survive the save.
    const entry = { ...storedEntry, key: currentSite?.country_code, value: subProperties };
    if (subListing !== undefined) entry.listing = subListing;
    if (subBox !== undefined) entry.box = subBox;
    const mergedProps = [...filteredProps, entry];

    const payload = {
      _id: idSubcategory,
      name: subcategory.name,
      slug: subcategory.slug,
      label: [
        {
          siteid: currentSite?._id,
          value: { label: labelsub, slug: labelslugsub },
        },
      ],
      properties: mergedProps,
      hostsProducts: subcategory.hostsProducts === true,
    };
    dispatch(updateSubategory(payload));
  };

  // SCRUM-2352: a delete the server refused because products or pages still
  // point at the node (the SCRUM-2350 409) opens the list instead of a toast;
  // appErrorMiddleware leaves that payload alone, so the message shows once.
  const showReferrers = (err: unknown) => {
    if (!isDeleteRefusal(err)) return;
    setAlertNode(
      <ReferrersDialog
        message={refusalMessage(err)}
        referrers={err.referrers}
        onClose={() => setAlertNode(null)}
      />
    );
  };

  // SCRUM-2114 owns these two hand-built confirms; SCRUM-2127 leaves their shape.
  const DeleteCategoryAlert = () => {
    setAlertNode(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to remove this Category?"
        customIcon={
          <div className="swal2-icon swal2-question swal2-animate-question-icon" style={{ display: "flex" }}>
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          dispatchForm(formActions.deleteConfirmed());
          setAlertNode(null);
          try {
            // SCRUM-840: only claim success once the delete actually resolves;
            // the central hub surfaces the error on failure.
            await dispatch(deleteCategory(idCategory)).unwrap();
            NotificationAlertOptions("success", "Category Deleted with success");
          } catch (err) {
            // A refusal with referrers gets the list (SCRUM-2352); every
            // other failure is surfaced by the central hub.
            showReferrers(err);
          }
        }}
        onCancel={() => setAlertNode(null)}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        {/* SCRUM-2373: the delete is catalogue-wide; say so, and name the
            per-site alternative. */}
        This deletes the category and its subcategories for all sites. To take
        it off this site only, use Site Categories → Remove.
      </ReactBSAlert>
    );
  };

  const DeleteSubcategoryAlert = () => {
    setAlertNode(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to remove this Subcategory?"
        customIcon={
          <div className="swal2-icon swal2-question swal2-animate-question-icon" style={{ display: "flex" }}>
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          dispatchForm(formActions.deleteConfirmed());
          setAlertNode(null);
          try {
            // SCRUM-840: refresh + success toast only once the delete resolves.
            await dispatch(deleteSubcategory(idSubcategory)).unwrap();
            dispatch(getCategories());
            NotificationAlertOptions("success", "Subcategory Deleted with success");
          } catch (err) {
            showReferrers(err);
          }
        }}
        onCancel={() => setAlertNode(null)}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        Delete Operation
      </ReactBSAlert>
    );
  };

  const closeUpdate = () => {
    dispatchForm(formActions.closed());
    dispatch(clearEntityPendingChange());
  };

  const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch({ query: (e.target.value || "").toLowerCase(), against: categoriesSlice.categories });
  };

  const onClickEnable = (e: React.ChangeEvent<HTMLInputElement>, element: { key: string }) => {
    dispatchForm(e.target.checked ? formActions.propertyAdded(element.key) : formActions.propertyRemoved(element.key));
  };

  const onClickShow = (e: React.ChangeEvent<HTMLInputElement>, element: { key: string }) => {
    dispatchForm(formActions.listingToggled({ key: element.key, checked: e.target.checked }));
  };

  const onClickBox = (e: React.ChangeEvent<HTMLInputElement>, element: { key: string }) => {
    dispatchForm(formActions.boxToggled({ key: element.key, checked: e.target.checked }));
  };

  return (
    <UpdateCategoriesView

      alertNode={alertNode}
      permission={permission}
      categories={categories}
      openedCollapses={openedCollapses}
      showUpdate={form.editor !== "none"}
      showCategoryUpdate={form.editor === "category"}
      showSubCategoryUpdate={form.editor === "subcategory"}
      category={category}
      categoryValidation={form.categoryValidation}
      subcategory={subcategory}
      subcategoryValidation={form.subcategoryValidation}
      labelsub={labelsub}
      labelslugsub={labelslugsub}
      allProperties={allProperties}
      subProperties={subProperties}
      subListing={subListing}
      subBox={subBox}
      currentSiteId={currentSite?._id}
      currentSiteCountryCode={currentSite?.country_code}
      entityPendingChange={entityPendingChange}
      pendingChangeReady={form.pendingChangeReady}
      unlockedFields={form.unlockedFields}
      onUnlock={handleUnlock}

      collapsesToggle={collapsesToggle}
      searchHandler={searchHandler}
      showUpdatingCategory={showUpdatingCategory}
      showUpdatingSubCategory={showUpdatingSubCategory}
      onChangeCategory={onChangeCategory}
      onChangeSubcategory={onChangeSubcategory}
      onClickUpdateCategory={onClickUpdateCategory}
      onClickUpdateSubcategory={onClickUpdateSubcategory}
      closeUpdate={closeUpdate}
      DeleteCategoryAlert={DeleteCategoryAlert}
      DeleteSubcategoryAlert={DeleteSubcategoryAlert}
      onClickEnable={onClickEnable}
      onClickShow={onClickShow}
      onClickBox={onClickBox}
    />
  );
};

export default UpdateCategoriesContainer;
